import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import { docsFor } from '../../fields/docs'
import { filterTypesFor } from '../../filters/inputs'
import { fieldKey } from '../examples'
import { filterParameter } from '../filter-examples'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table'

/**
 * What one field is called, what it holds, and what may be asked of it.
 *
 * The description comes back from the field docs layer as a key and its values,
 * so the sentence is chosen there and said here -- which is what lets that layer
 * stay a pure function of a field.
 */
export function FieldsTable({
  fields,
  userFieldNames,
}: {
  fields: readonly Field[]
  userFieldNames: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">{t('apiDocs.fieldId')}</TableHead>
            <TableHead>{t('apiDocs.fieldName')}</TableHead>
            <TableHead>{t('apiDocs.fieldType')}</TableHead>
            <TableHead>{t('apiDocs.fieldDescription')}</TableHead>
            <TableHead>{t('apiDocs.compatibleFilters')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field) => {
            const docs = docsFor(field.type)
            const described = docs.description(field)
            const filters = filterTypesFor(field)

            return (
              <TableRow key={field.id}>
                <TableCell className="align-top font-mono text-xs">{field.id}</TableCell>
                <TableCell className="align-top font-mono text-xs break-all">
                  {fieldKey(field, { userFieldNames })}
                </TableCell>
                <TableCell className="text-muted-foreground align-top font-mono text-xs">
                  {/* A field the backend writes has no type to send, and saying
                      so is different from leaving the cell blank. */}
                  {docs.dataType(field) ?? t('apiDocs.noFilters')}
                </TableCell>
                <TableCell className="text-muted-foreground align-top text-sm">
                  {t(
                    `fieldDocs.${described.key}` as 'fieldDocs.text',
                    described.values ?? {},
                  )}
                  {'select_options' in field && <SelectOptions field={field} />}
                </TableCell>
                <TableCell className="align-top">
                  {filters.length === 0 ? (
                    <span className="text-muted-foreground text-xs">
                      {t('apiDocs.noFilters')}
                    </span>
                  ) : (
                    <ul className="text-muted-foreground space-y-0.5 font-mono text-xs">
                      {filters.map((filter) => (
                        <li key={filter.type}>{filter.type}</li>
                      ))}
                    </ul>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

/**
 * The options a select column offers, with the ids a request would send.
 *
 * Upstream builds this as an HTML string inside the field type's description
 * and hands it to `v-html`. Rendered as elements here, so an option somebody
 * named `<b>` is a name rather than markup.
 */
function SelectOptions({ field }: { field: Field }) {
  const options = (field as { select_options?: { id: number; value: string }[] })
    .select_options
  if (options === undefined || options.length === 0) return null

  return (
    <ul className="mt-2 space-y-0.5">
      {options.map((option) => (
        <li key={option.id} className="font-mono text-xs">
          {option.id} — {option.value}
        </li>
      ))}
    </ul>
  )
}

/**
 * Every condition a listing can be narrowed by, for the columns of one table.
 *
 * Per table rather than one list for the database, because which conditions
 * exist is a question about a column: `has_file_type` means nothing to a number
 * and `higher_than` means nothing to an attachment.
 */
export function FiltersTable({
  fields,
  userFieldNames,
}: {
  fields: readonly Field[]
  userFieldNames: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="space-y-6">
      {fields.map((field) => {
        const filters = filterTypesFor(field)
        if (filters.length === 0) return null
        const key = fieldKey(field, { userFieldNames })

        return (
          <div key={field.id} className="space-y-2">
            <h4 className="text-sm font-medium">
              {t('apiDocs.filterFor', { name: field.name })}
            </h4>
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('apiDocs.filterName')}</TableHead>
                    <TableHead>{t('apiDocs.filterExample')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filters.map((filter) => (
                    <TableRow key={filter.type}>
                      <TableCell className="align-top text-sm">
                        {t(`viewFilter.${filter.type}` as 'viewFilter.equal')}
                      </TableCell>
                      <TableCell className="align-top font-mono text-xs break-all">
                        {filterParameter(filter.type, field, key)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const STATUSES = ['200', '400', '401', '404', '413', '500', '502', '503'] as const

/** What each status means for these endpoints, rather than in general. */
export function ErrorsTable() {
  const { t } = useTranslation()

  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table>
        <TableBody>
          {STATUSES.map((status) => (
            <TableRow key={status}>
              <TableCell className="w-16 align-top font-mono text-xs">{status}</TableCell>
              <TableCell className="text-muted-foreground align-top text-sm">
                {t(`apiDocsErrors.${status}`)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
